import { useCallback, useEffect, useState } from 'react';
import { Copy, Eye, EyeOff, Trash2 } from 'lucide-react';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { AppCard } from '../../components/ui/AppCard';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/TextField';
import { countByChannel, clearAll } from '../../lib/signalDb';
import { formatBytes, maskValue, readLocalEntries, type LocalEntry } from '../../lib/localData';

// Read-only-ish view of what this browser stores for Zmist: the local
// key/value store (preferences, consent, keys) and the IndexedDB message
// cache. Secret-looking values are masked until revealed. The server stays the
// source of truth - everything here can be deleted and is rebuilt on demand.
export function DatabaseScreen() {
  const [entries, setEntries] = useState<LocalEntry[]>([]);
  const [filter, setFilter] = useState('');
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<Record<string, number>>({});
  const [quota, setQuota] = useState<{ usage: number; quota: number } | null>(null);
  const [copied, setCopied] = useState('');

  const refresh = useCallback(async () => {
    setEntries(readLocalEntries());
    setRows(await countByChannel());
    try {
      const e = await navigator.storage?.estimate?.();
      setQuota(e?.usage !== undefined && e.quota !== undefined ? { usage: e.usage, quota: e.quota } : null);
    } catch {
      setQuota(null);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const shown = entries.filter((e) => e.key.toLowerCase().includes(filter.trim().toLowerCase()));
  const totalRows = Object.values(rows).reduce((a, b) => a + b, 0);
  const toggle = (k: string) =>
    setRevealed((p) => {
      const n = new Set(p);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const copy = async (e: LocalEntry) => {
    try {
      await navigator.clipboard.writeText(e.value);
      setCopied(e.key);
    } catch {
      /* clipboard unavailable */
    }
  };
  const remove = (k: string) => {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
    void refresh();
  };

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Database" backTo="/settings/stats" />

      <FormSection title="This browser">
        <AppCard compact className="flex flex-col gap-1 text-[13px]">
          <div className="flex justify-between"><span>Stored entries</span><span className="font-mono">{entries.length}</span></div>
          <div className="flex justify-between"><span>Cached messages</span><span className="font-mono">{totalRows.toLocaleString()} in {Object.keys(rows).length} channels</span></div>
          {quota && <div className="flex justify-between"><span>Storage used</span><span className="font-mono">{formatBytes(quota.usage)} of {formatBytes(quota.quota)}</span></div>}
        </AppCard>
        <p className="text-xs text-black/45 dark:text-white/45 m-0">Your account data lives on the server; this is only this device's copy and preferences.</p>
      </FormSection>

      <FormSection title="Stored values">
        <TextField aria-label="Filter keys" placeholder="Filter, e.g. chart or security" value={filter} onChange={(e) => setFilter(e.target.value)} spellCheck={false} />
        <div className="flex flex-col gap-1.5">
          {shown.length === 0 && <span className="text-sm text-black/45 dark:text-white/45">Nothing stored{filter ? ' matches that filter' : ''}.</span>}
          {shown.map((e) => {
            const hidden = e.sensitive && !revealed.has(e.key);
            return (
              <div key={e.key} className="rounded-xl border border-black/10 dark:border-white/10 px-3 py-2 flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <code className="font-mono text-[12px] font-medium truncate flex-1" title={e.key}>{e.key}</code>
                  <span className="text-[11px] text-black/40 dark:text-white/40">{formatBytes(e.bytes)}</span>
                  {e.sensitive && (
                    <button type="button" aria-label={hidden ? `Reveal ${e.key}` : `Hide ${e.key}`} onClick={() => toggle(e.key)} className="text-black/45 dark:text-white/45">
                      {hidden ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                  )}
                  <button type="button" aria-label={`Copy ${e.key}`} onClick={() => void copy(e)} className="text-black/45 dark:text-white/45">
                    <Copy size={15} />
                  </button>
                  <button type="button" aria-label={`Delete ${e.key}`} onClick={() => remove(e.key)} className="text-red-500/80">
                    <Trash2 size={15} />
                  </button>
                </div>
                <code className="font-mono text-[12px] text-black/60 dark:text-white/60 break-all line-clamp-3">{hidden ? maskValue(e.value) : e.value}</code>
                {copied === e.key && <span className="text-[11px] text-emerald-600">Copied</span>}
              </div>
            );
          })}
        </div>
      </FormSection>

      <FormSection title="Message cache">
        {Object.entries(rows).sort((a, b) => b[1] - a[1]).map(([ch, n]) => (
          <div key={ch} className="flex justify-between text-[13px]">
            <span className="truncate">{ch}</span>
            <span className="font-mono">{n.toLocaleString()}</span>
          </div>
        ))}
        <Button variant="destructive" disabled={totalRows === 0} onClick={() => void clearAll().then(refresh)}>
          Clear message cache
        </Button>
      </FormSection>
    </div>
  );
}
